import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Badge,
  BedroomChild,
  CorporateFare,
  DateRange,
  Person,
  Task
} from '@mui/icons-material';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';
import { UseQueryOptions } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  ALL_FACILITIES,
  useSelectedFacilityId
} from '@/hooks/useFacilitiesQuery';
import { useCrmTaskInstancesQuery } from '@/pages/CRM/hooks/useCrmTaskInstancesQuery';
import { useIsBedBoardEnabled } from '@/pages/CRM/Screener/hooks/useIsBedBoardEnabled';
import { useIsScreenerLeadWaitlistEnabled } from '@/pages/CRM/Screener/hooks/useIsScreenerLeadWaitlistEnabled';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import useNavigateToScreenerAllFacilities from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useNavigateToScreenerAllFacilitites';
import { RoutePath } from '@/routes/RoutePath';
import { ExacareFeature, FeatureFlagService } from '@/utils/featureFlagService';
import { generatePathWithDomain } from '@/utils/generatePathWithDomain';

import { SidebarItemBadgeProps } from './SidebarItemBadge';

export interface SidebarItem {
  icon: React.ReactNode;
  text: string;
  path: string;
  badgeProps?: Partial<SidebarItemBadgeProps>;
  showOnMobile?: boolean;
  onClick?: () => void;
}

const useCrmTasksBadgeCount = () => {
  const pathname = useLocation().pathname;
  const facilityId = useSelectedFacilityId();
  const commonSearchQuery = {
    // Time period to show incomplete tasks (3 days ago to now)
    from_date: dayjs().subtract(3, 'days').startOf('day').toISOString(),
    to_date: dayjs().endOf('day').toISOString()
  };
  const commonOptions: UseQueryOptions<any> = {
    enabled: pathname.startsWith('/crm') && facilityId !== ALL_FACILITIES
  };
  const { data: leadTaskCount } = useCrmTaskInstancesQuery().useCount(
    {
      ...commonSearchQuery,
      relation_type: 'LEAD'
    },
    commonOptions
  );
  const { data: sourceTaskCount } = useCrmTaskInstancesQuery().useCount(
    {
      ...commonSearchQuery,
      relation_type: 'SCREENER_SOURCE'
    },
    commonOptions
  );
  const { data: sourceContactTaskCount } = useCrmTaskInstancesQuery().useCount(
    {
      ...commonSearchQuery,
      relation_type: 'SCREENER_SOURCE_CONTACT'
    },
    commonOptions
  );
  return [leadTaskCount, sourceTaskCount, sourceContactTaskCount].reduce(
    (tally, model) => (tally += model?.incompleteTaskCount ?? 0),
    0
  );
};

export const useGetSidebarCRMItems = (): SidebarItem[] => {
  const currentUser = useCurrentUser().data!;
  const isBedBoardEnabled = useIsBedBoardEnabled();
  const crmTasksBadgeCount = useCrmTasksBadgeCount();
  const isWaitlistEnabled = useIsScreenerLeadWaitlistEnabled();
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const navigateToScreenerAllFacilities = useNavigateToScreenerAllFacilities(
    RoutePath.CRMScreenerAllLeads
  );
  return [
    ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER)
      ? [
          {
            icon: <ScreenSearchDesktopIcon />,
            text: 'Screener',
            path: RoutePath.CRMScreenerAllLeads,
            showOnMobile: true,
            onClick: isAllFacilitiesSelected
              ? navigateToScreenerAllFacilities
              : undefined
          }
        ]
      : []),
    ...(isWaitlistEnabled
      ? [
          {
            icon: <ContactPageIcon />,
            text: 'Waitlist',
            path: RoutePath.CRMWaitlist,
            showOnMobile: true
          }
        ]
      : []),
    ...(isBedBoardEnabled
      ? [
          {
            icon: <Person />,
            text: 'Residents',
            path: RoutePath.CrmScreenerResidentsAllResidents,
            showOnMobile: true
          }
        ]
      : []),
    ...(currentUser.isScreenerCustomer() && isBedBoardEnabled
      ? [
          {
            icon: <BedroomChild />,
            text: 'Bed Board',
            path: RoutePath.CRMBedboard,
            showOnMobile: true
          }
        ]
      : []),
    ...(currentUser.isScreenerCustomer() &&
    FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER_TASKS)
      ? [
          {
            icon: <DateRange />,
            text: 'Tasks',
            path: RoutePath.CRMTasks,
            showOnMobile: true,
            badgeProps: {
              badgeContent: crmTasksBadgeCount
            }
          }
        ]
      : currentUser.isScreenerCustomer()
        ? []
        : [
            {
              icon: <DateRange />,
              text: 'Tasks',
              path: RoutePath.CRMTasks,
              showOnMobile: true,
              badgeProps: {
                badgeContent: crmTasksBadgeCount
              }
            }
          ]),
    ...(currentUser.isScreenerCustomer()
      ? [
          {
            icon: <Badge />,
            text: 'Sources',
            path: RoutePath.CRMSources,
            showOnMobile: true
          }
        ]
      : []),
    ...(FeatureFlagService.isEnabled(ExacareFeature.DATA_CENTER_TAB) &&
    currentUser.isDataCenterEnabled()
      ? [
          {
            icon: <Task />,
            text: 'Data Center',
            path: generatePathWithDomain(RoutePath.DataCenterDashboards)
          }
        ]
      : []),
    ...(currentUser.hasMinimumUserRole('L1') ||
    currentUser.isEditUserTypeNotificationPreferencesEnabled()
      ? [
          {
            icon: <CorporateFare />,
            text: 'Admin Hub',
            path: generatePathWithDomain(RoutePath.AdminHub)
          }
        ]
      : [])
  ];
};
